/**
 * Shopify Dashboard React Hooks
 * Custom hooks for data fetching and state management
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getDashboardSummary,
  getClickAnalytics,
  getConversionMetrics,
  getPersonaAnalytics,
  getProductAnalytics,
  getTrendingProducts,
  listMagicLinks,
  getMagicLinkDetails,
  createMagicLink,
  deactivateMagicLink,
  syncShopifyOrders,
  type DashboardSummary,
  type ClickAnalytics,
  type ConversionMetrics,
  type PersonaAnalytics,
  type ProductAnalytics,
  type TrendingProductsResponse,
  type MagicLinkListResponse,
  type MagicLinkDetails,
  type MagicLinkRequest,
  type MagicLinkResponse,
  type SyncOrdersResponse,
} from '../utils/shopify';

// ============================================================================
// Types
// ============================================================================

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

interface UseAsyncOptions {
  immediate?: boolean;
}

// ============================================================================
// Generic Async Hook
// ============================================================================

function useAsync<T, Args extends any[]>(
  asyncFunction: (...args: Args) => Promise<T>,
  options: UseAsyncOptions = {}
) {
  const { immediate = false } = options;

  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: immediate,
    error: null,
  });

  const execute = useCallback(
    async (...args: Args) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const data = await asyncFunction(...args);
        setState({ data, loading: false, error: null });
        return data;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        setState({ data: null, loading: false, error: err });
        throw err;
      }
    },
    [asyncFunction]
  );

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, execute, reset };
}

// ============================================================================
// Dashboard Hook
// ============================================================================

export function useShopifyDashboard(days: number = 30) {
  const [state, setState] = useState<AsyncState<DashboardSummary>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchDashboard = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await getDashboardSummary(days);
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [days]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { ...state, refetch: fetchDashboard };
}

// ============================================================================
// Click Analytics Hook
// ============================================================================

export interface UseClickAnalyticsParams {
  magicLinkId?: string;
  personaId?: string;
  productId?: string;
  startDate?: string;
  endDate?: string;
}

export function useClickAnalytics(params: UseClickAnalyticsParams = {}) {
  const [state, setState] = useState<AsyncState<ClickAnalytics>>({
    data: null,
    loading: true,
    error: null,
  });

  const paramsKey = useMemo(
    () => JSON.stringify(params),
    [
      params.magicLinkId,
      params.personaId,
      params.productId,
      params.startDate,
      params.endDate,
    ]
  );

  const fetchAnalytics = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await getClickAnalytics({
        magic_link_id: params.magicLinkId,
        persona_id: params.personaId,
        product_id: params.productId,
        start_date: params.startDate,
        end_date: params.endDate,
      });
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { ...state, refetch: fetchAnalytics };
}

// ============================================================================
// Conversion Metrics Hook
// ============================================================================

export interface UseConversionMetricsParams {
  personaId?: string;
  productId?: string;
  days?: number;
}

export function useConversionMetrics(params: UseConversionMetricsParams = {}) {
  const [state, setState] = useState<AsyncState<ConversionMetrics>>({
    data: null,
    loading: true,
    error: null,
  });

  const paramsKey = useMemo(
    () => JSON.stringify(params),
    [params.personaId, params.productId, params.days]
  );

  const fetchMetrics = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await getConversionMetrics({
        persona_id: params.personaId,
        product_id: params.productId,
        days: params.days,
      });
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return { ...state, refetch: fetchMetrics };
}

// ============================================================================
// Persona Analytics Hook
// ============================================================================

export function usePersonaAnalytics(personaId: string, days: number = 30) {
  const [state, setState] = useState<AsyncState<PersonaAnalytics>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchAnalytics = useCallback(async () => {
    if (!personaId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await getPersonaAnalytics(personaId, days);
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [personaId, days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { ...state, refetch: fetchAnalytics };
}

// ============================================================================
// Product Analytics Hook
// ============================================================================

export function useProductAnalytics(productId: string) {
  const [state, setState] = useState<AsyncState<ProductAnalytics>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchAnalytics = useCallback(async () => {
    if (!productId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await getProductAnalytics(productId);
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [productId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { ...state, refetch: fetchAnalytics };
}

// ============================================================================
// Trending Products Hook
// ============================================================================

export function useTrendingProducts(limit: number = 10, days: number = 7) {
  const [state, setState] = useState<AsyncState<TrendingProductsResponse>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchTrending = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await getTrendingProducts({ limit, days });
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [limit, days]);

  useEffect(() => {
    fetchTrending();
  }, [fetchTrending]);

  return { ...state, refetch: fetchTrending };
}

// ============================================================================
// Magic Links Hook
// ============================================================================

export interface UseMagicLinksParams {
  personaId?: string;
  productId?: string;
  limit?: number;
}

export function useMagicLinks(params: UseMagicLinksParams = {}) {
  const [state, setState] = useState<AsyncState<MagicLinkListResponse>>({
    data: null,
    loading: true,
    error: null,
  });

  const paramsKey = useMemo(
    () => JSON.stringify(params),
    [params.personaId, params.productId, params.limit]
  );

  const fetchLinks = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await listMagicLinks({
        persona_id: params.personaId,
        product_id: params.productId,
        limit: params.limit,
      });
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  return { ...state, refetch: fetchLinks };
}

// ============================================================================
// Single Magic Link Hook
// ============================================================================

export function useMagicLink(magicLinkId: string | null) {
  const [state, setState] = useState<AsyncState<MagicLinkDetails>>({
    data: null,
    loading: false,
    error: null,
  });

  const fetchLink = useCallback(async () => {
    if (!magicLinkId) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await getMagicLinkDetails(magicLinkId);
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, [magicLinkId]);

  useEffect(() => {
    fetchLink();
  }, [fetchLink]);

  return { ...state, refetch: fetchLink };
}

// ============================================================================
// Magic Link Mutation Hook
// ============================================================================

export function useMagicLinkMutations() {
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(
    async (request: MagicLinkRequest): Promise<MagicLinkResponse> => {
      setCreating(true);
      setError(null);

      try {
        const result = await createMagicLink(request);
        setCreating(false);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setCreating(false);
        throw error;
      }
    },
    []
  );

  const deactivate = useCallback(async (magicLinkId: string): Promise<void> => {
    setDeactivating(true);
    setError(null);

    try {
      await deactivateMagicLink(magicLinkId);
      setDeactivating(false);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setDeactivating(false);
      throw error;
    }
  }, []);

  return {
    create,
    deactivate,
    creating,
    deactivating,
    loading: creating || deactivating,
    error,
  };
}

// ============================================================================
// Order Sync Hook
// ============================================================================

export function useOrderSync() {
  const [state, setState] = useState<AsyncState<SyncOrdersResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const sync = useCallback(async (sinceDate?: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const data = await syncShopifyOrders(sinceDate);
      setState({ data, loading: false, error: null });
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setState({ data: null, loading: false, error: err });
      throw err;
    }
  }, []);

  return { ...state, sync };
}

// ============================================================================
// Combined Dashboard Data Hook
// ============================================================================

export interface DashboardData {
  summary: DashboardSummary | null;
  analytics: ClickAnalytics | null;
  conversions: ConversionMetrics | null;
  trending: TrendingProductsResponse | null;
  magicLinks: MagicLinkListResponse | null;
}

export function useFullDashboard(personaId?: string, days: number = 30) {
  const [data, setData] = useState<DashboardData>({
    summary: null,
    analytics: null,
    conversions: null,
    trending: null,
    magicLinks: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [summary, analytics, conversions, trending, magicLinks] =
        await Promise.all([
          getDashboardSummary(days),
          getClickAnalytics({ persona_id: personaId }),
          getConversionMetrics({ persona_id: personaId, days }),
          getTrendingProducts({ limit: 10, days: 7 }),
          listMagicLinks({ persona_id: personaId, limit: 50 }),
        ]);

      setData({ summary, analytics, conversions, trending, magicLinks });
      setLoading(false);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setLoading(false);
      throw error;
    }
  }, [personaId, days]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, refetch: fetchAll };
}

// ============================================================================
// Date Range Hook
// ============================================================================

export type DateRangePreset =
  | '7d'
  | '30d'
  | '90d'
  | '365d'
  | 'mtd'
  | 'ytd'
  | 'custom';

export function useDateRange(initialPreset: DateRangePreset = '30d') {
  const [preset, setPreset] = useState<DateRangePreset>(initialPreset);
  const [customRange, setCustomRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });

  const dateRange = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (preset) {
      case '7d':
        return {
          start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
          end: today,
          days: 7,
        };
      case '30d':
        return {
          start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
          end: today,
          days: 30,
        };
      case '90d':
        return {
          start: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000),
          end: today,
          days: 90,
        };
      case '365d':
        return {
          start: new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000),
          end: today,
          days: 365,
        };
      case 'mtd':
        return {
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: today,
          days: Math.ceil(
            (today.getTime() -
              new Date(now.getFullYear(), now.getMonth(), 1).getTime()) /
              (24 * 60 * 60 * 1000)
          ),
        };
      case 'ytd':
        return {
          start: new Date(now.getFullYear(), 0, 1),
          end: today,
          days: Math.ceil(
            (today.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) /
              (24 * 60 * 60 * 1000)
          ),
        };
      case 'custom':
        if (customRange.start && customRange.end) {
          return {
            start: customRange.start,
            end: customRange.end,
            days: Math.ceil(
              (customRange.end.getTime() - customRange.start.getTime()) /
                (24 * 60 * 60 * 1000)
            ),
          };
        }
        return { start: today, end: today, days: 1 };
      default:
        return {
          start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000),
          end: today,
          days: 30,
        };
    }
  }, [preset, customRange]);

  return {
    preset,
    setPreset,
    customRange,
    setCustomRange,
    dateRange,
    startDateISO: dateRange.start.toISOString(),
    endDateISO: dateRange.end.toISOString(),
  };
}
